import Phaser from 'phaser';
import { FONT_STACK } from '@/game/config';
import type { PromptParts } from '@/game/ui/promptEmphasis';
import { drawPromptMarker, MARKER_SCALE, type PromptMarker } from '@/game/ui/promptMarker';

/**
 * The rule banner with its key word picked out — three Texts in a row, since Phaser Text has no
 * inline styling. Returns a Container sized to its contents, so it wears the same pill, tweens
 * and anchors the help button exactly as the plain single Text does.
 *
 * Weight only, same gold: the banner already reads as one phrase, and a second colour inside it
 * competes with the pill's own palette (teal, the obvious pick, already means "new record" here).
 * Bold says "this word" without saying "a different kind of thing".
 *
 * Container gets originX/originY = 0.5 from the ComputedSize component, which is what lets the
 * pill centre itself on the row the same way it does on a single Text.
 *
 * Shared by the board and Survival: both show the same sentence from the same source (a pack
 * class's `prompt`), and differ only in font size.
 *
 * `marker` is the optional coloured shape the "Der, Die, Das" lesson puts in front of its key word
 * (src/game/ui/promptMarker.ts). It goes between `before` and `bold` — i.e. immediately in front of
 * the article — and is a drawn object, never a character in the sentence, so the string that is
 * read aloud is unchanged by it.
 */
export function createSplitPromptBanner(
  scene: Phaser.Scene,
  parts: PromptParts,
  x: number,
  y: number,
  fontSize = '36px',
  marker: PromptMarker | null = null,
): Phaser.GameObjects.Container {
  const PAD_X = 18;
  const PAD_Y = 10;
  // Only on the right: the sentence's own trailing space ("Schnappe dir alle ") already opens the
  // gap on the left, and a second one there would push the marker off its word.
  const MARKER_GAP = 8;
  const base = { fontFamily: FONT_STACK, fontSize, color: '#ffd166' };
  const key = { ...base, fontStyle: 'bold' };

  // Origin (0, 0.5) so the three sit on one baseline and butt up against each other; the row is
  // then shifted left by half its width to centre the whole thing on x.
  const mk = (text: string, style: Phaser.Types.GameObjects.Text.TextStyle) =>
    scene.add.text(0, 0, text, style).setOrigin(0, 0.5);
  const [before, bold, after] = [mk(parts.before, base), mk(parts.bold, key), mk(parts.after, base)];
  const segments = [before, bold, after];

  const glyph = marker
    ? drawPromptMarker(scene, marker, parseFloat(fontSize) * MARKER_SCALE)
    : null;
  const markerW = glyph ? glyph.width + MARKER_GAP : 0;

  const textW = segments.reduce((sum, t) => sum + t.width, 0) + markerW;
  const textH = Math.max(...segments.map((t) => t.height));
  let cursor = -textW / 2;
  before.x = cursor;
  cursor += before.width;
  if (glyph) {
    // The marker is centred on its own origin, unlike the origin-(0, 0.5) Texts around it.
    glyph.setPosition(cursor + glyph.width / 2, 0);
    cursor += markerW;
  }
  bold.x = cursor;
  cursor += bold.width;
  after.x = cursor;

  const box = scene.add.container(x, y, glyph ? [before, glyph, bold, after] : segments);
  // The pill measures itself off this, so the container has to know its own box — a Container's
  // width is 0 until it is told, however many children it holds.
  box.setSize(textW + PAD_X * 2, textH + PAD_Y * 2);
  return box;
}

/**
 * Widest the sentence line may run before it wraps. The pill grows to fit its text, and the "?"
 * button hangs 36px past the pill's right edge with a 26px radius — so a banner centred on
 * GAME_WIDTH/2 must stay under (1280/2 - 36 - 26) * 2 = 1156px of pill, padding included, or the
 * "?" walks off the canvas. 1000 leaves room and wraps the long sentences onto a second line, which
 * reads better than one 1100px ribbon anyway.
 */
const STACK_WRAP_W = 1000;

/**
 * A two-part banner: the sentence on top, the question about it underneath — the comprehension
 * lesson's board (specs/comprehension-mode.md §3). Returns a Container sized to its contents, so it
 * wears the same pill, tweens and anchors the "?" exactly as the single-line banners do.
 *
 * **Sentence first, then the question**, which is the order of every reading exercise on paper and,
 * more to the point, the only order that works when the banner is spoken: a question heard before
 * you know what it is about has to be held in memory instead of applied. It costs the child a
 * moment of "what am I looking for" while reading — the trade a printed comprehension task makes
 * too.
 *
 * Two Texts rather than one with a newline, because the two lines do different jobs and Phaser Text
 * has no inline styling: the sentence is the material to read (30px, near-white) and the question
 * is the task (36px, the banner gold every other lesson uses). One uniform block would leave the
 * child hunting for where the sentence ends.
 *
 * The sentence wraps; the question does not. A question long enough to wrap is a question that
 * should have been shorter, and letting it wrap would push the pill down over the board.
 */
export function createStackedPromptBanner(
  scene: Phaser.Scene,
  sentence: string,
  question: string,
  x: number,
  y: number,
): Phaser.GameObjects.Container {
  const PAD_X = 18;
  const PAD_Y = 12;
  const GAP = 8;

  const s = scene.add.text(0, 0, sentence, {
    fontFamily: FONT_STACK, fontSize: '30px', color: '#eef2f6',
    align: 'center', wordWrap: { width: STACK_WRAP_W },
  }).setOrigin(0.5, 0);
  const q = scene.add.text(0, 0, question, {
    fontFamily: FONT_STACK, fontSize: '36px', color: '#ffd166', fontStyle: 'bold',
    align: 'center',
  }).setOrigin(0.5, 0);

  const textW = Math.max(q.width, s.width);
  const textH = s.height + GAP + q.height;
  // Origin (0.5, 0) on both, then the pair is lifted by half its height so the whole stack is
  // centred on y — the same trick the split banner plays horizontally.
  s.y = -textH / 2;
  q.y = s.y + s.height + GAP;

  const box = scene.add.container(x, y, [s, q]);
  box.setSize(textW + PAD_X * 2, textH + PAD_Y * 2);
  return box;
}
